import {
  NotificationType,
  showNotification,
  showToast
} from '@/utils/Notification'
import axios from 'axios'
import { ActionTree } from 'vuex'
import { DoctorPayload } from '../doctor/types'
import { Response } from '../response'
import { ApiVersion, RootState } from '../types'
import { PatientMutations } from './mutations'
import {
  ClinicalHistoryRequest,
  ConsultationListItem,
  MorbidityRegistryRequest,
  CurrentConditionRequest,
  ObservationRequest,
  ObstetricEvent,
  ObstetricEventRequestPayload,
  PatientDetail,
  PatientListRequest,
  PatientListResponse,
  PatientState,
  ResolutionEventRequest,
  ResolutiveEvent,
  SectionForm,
  UploadStudyFileRequest,
  UploadUltrasoundFileRequest,
  DeleteUltrasoundFileRequest,
  TransfusionRequest,
  ConsultoryDetailsByConsultation
} from './types'

export const actions: ActionTree<PatientState, RootState> = {
  //Get patients list
  async getPatientsList({ commit }, request: PatientListRequest) {
    try {
      let baseUrl = `${ApiVersion.V2}/Patients?PageNumber=${request.PageNumber}&PageSize=${request.PageSize}`
      if (request.FilterBy != null) {
        baseUrl += `&FilterBy=${request.FilterBy}`
      }
      if (request.FilterThenBy != null) {
        baseUrl += `&FilterThenBy=${request.FilterThenBy}`
      }

      if (request.SearchPayload != null) {
        baseUrl += `&SearchPayload=${request.SearchPayload}`
      }

      if (request.OrderBy != null) {
        baseUrl += `&OrderBy=${request.OrderBy}`
      }

      if (request.Desc != null) {
        baseUrl += `&Desc=${request.Desc}`
      }

      commit(PatientMutations.SET_LOADER_PATIENTS_LIST, true)
      const { data } = await axios.get(baseUrl)
      const response: PatientListResponse = data
      commit(PatientMutations.SET_PATIENTS_LIST, response)
    } catch (error) {
      console.error(error)
    } finally {
      commit(PatientMutations.SET_LOADER_PATIENTS_LIST, false)
    }
  },

  //Get patient details
  async getPatientById({ commit }, request: DoctorPayload<number>) {
    try {
      if (!(request.hideLoad ?? false))
        commit(PatientMutations.SET_LOADER_PATIENT, true)
      const { data } = await axios.get(
        `${ApiVersion.V2}/Patients/${request.payload}`
      )
      const response: Response<PatientDetail> = data
      commit(PatientMutations.SET_CURRENT_PATIENT, response.data)
      console.log(response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(PatientMutations.SET_LOADER_PATIENT, false)
    }
  },

  //Set patien in observation
  async setPatienInObservation({ dispatch }, request: ObservationRequest) {
    try {
      await axios.patch(
        `${ApiVersion.V1}/Patients/${request.patientId}/inobservation?isInObservation=${request.observation}`
      )
      const patientRequest: DoctorPayload<number> = {
        entityId: request.doctorId,
        payload: request.patientId
      }
      showNotification(
        NotificationType.SUCCESS,
        'Se cambió el estado del paciente'
      )
      dispatch('getPatientById', patientRequest)
    } catch (error) {
      console.error(error)
    }
  },

  //Get consultations list from patient
  async getConsultations({ commit }, patientId: number) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Patients/${patientId}/consultations`
      )
      const response: Response<Array<ConsultationListItem>> = data
      commit(PatientMutations.SET_PATIENT_CONSULTATIONS, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Get resolutive event of patient
  async getResolutiveEvent({ commit }, patientId: Number) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Patients/${patientId}/resolutive-event`
      )
      const response: Response<ResolutiveEvent> = data
      commit(PatientMutations.SET_RESOLUTIVE_EVENT, response.data)
    } catch (error) {
      commit(PatientMutations.SET_RESOLUTIVE_EVENT, {})
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Get resolutive event of patient
  async getObstetricEvent({ commit }, obstetricEventId: Number) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/ResolutionEvents/${obstetricEventId}`
      )
      const response: Response<ObstetricEvent> = data
      commit(PatientMutations.SET_OBSTETRIC_EVENT, response.data)
    } catch (error) {
      commit(PatientMutations.SET_OBSTETRIC_EVENT, {})
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Get morbility event of patient
  async getMorbidityRegistry({ commit }, morbidityEventId: Number) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/MorbidityRegistries/${morbidityEventId}/details`
      )
      const response: Response<SectionForm[]> = data
      commit(PatientMutations.SET_MORBIDITY_REGISTRY, response.data)
    } catch (error) {
      commit(PatientMutations.SET_MORBIDITY_REGISTRY, [])
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Set resolutive event of patient
  async setObstetricEvent(
    { dispatch, commit },
    payload: ObstetricEventRequestPayload
  ) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.put(
        `${ApiVersion.V1}/ResolutionEvents/${payload.obstetricEventId}`,
        payload.obstetricEventRequest
      )
      const response: Response<string> = data
      dispatch('getResolutiveEvent', payload.patientId)
      return response.succeeded
    } catch (error) {
      dispatch('getResolutiveEvent', payload.patientId)
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Create morbidity event of patient
  async createMorbidityRegistry(
    { dispatch, commit },
    payload: ResolutionEventRequest
  ) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.post(
        `${ApiVersion.V1}/MorbidityRegistries/create/${payload.resolutionEventId}`
      )
      const response: Response<string> = data
      await dispatch('getResolutiveEvent', payload.patientId)
      return response.succeeded
    } catch (error) {
      await dispatch('getResolutiveEvent', payload.patientId)
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Create end event of patient
  async endMorbidityRegistry(
    { dispatch, commit },
    payload: ResolutionEventRequest
  ) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.post(
        `${ApiVersion.V1}/MorbidityRegistries/end/${payload.resolutionEventId}`
      )
      const response: Response<string> = data
      await dispatch('getResolutiveEvent', payload.patientId)
      return response.succeeded
    } catch (error) {
      await dispatch('getResolutiveEvent', payload.patientId)
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Ser morbidity event item
  async setMorbidityRegistryItem(
    { dispatch, commit },
    payload: MorbidityRegistryRequest
  ) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.patch(
        `${ApiVersion.V1}/MorbidityRegistries/${payload.morbidityRegistryId}/details`,
        payload.item
      )
      const response: Response<string> = data
      if (response.succeeded) {
        showToast(NotificationType.SUCCESS, response.message)
      } else {
        showToast(NotificationType.DANGER, response.message)
      }
      dispatch('getMorbidityRegistry', payload.morbidityRegistryId)
      return response.succeeded
    } catch (error) {
      showToast(NotificationType.DANGER, 'Error al actualizar el campo')
      dispatch('getMorbidityRegistry', payload.morbidityRegistryId)
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Get clinic history of patient
  async getClinicalHistory({ commit }, patientId: Number) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/ClinicalHistories/${patientId}/details`
      )
      const response: Response<SectionForm[]> = data
      commit(PatientMutations.SET_CLINICAL_HISTORY, response.data)
    } catch (error) {
      commit(PatientMutations.SET_CLINICAL_HISTORY, [])
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Create morbidity event of patient
  async createClinicalHistory({ dispatch, commit }, patientId: number) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.post(
        `${ApiVersion.V1}/ClinicalHistories/${patientId}/create`
      )
      const response: Response<string> = data
      dispatch('getClinicalHistory', patientId)
      return response.succeeded
    } catch (error) {
      dispatch('getClinicalHistory', patientId)
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Set clinical history item
  async setClinicalHistoryItem(
    { dispatch, commit },
    payload: ClinicalHistoryRequest
  ) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.patch(
        `${ApiVersion.V1}/ClinicalHistories/${payload.patientId}/details`,
        payload.item
      )
      const response: Response<string> = data
      if (response.succeeded) {
        showToast(NotificationType.SUCCESS, response.message)
      } else {
        showToast(NotificationType.DANGER, response.message)
      }
      dispatch('getClinicalHistory', payload.patientId)
      return response.succeeded
    } catch (error) {
      showToast(NotificationType.DANGER, 'Error al actualizar el campo')
      dispatch('getClinicalHistory', payload.patientId)
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Get clinic history of patient
  async getCurrentCondition(
    { commit },
    consultationId: Number
  ): Promise<boolean> {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Consultations/${consultationId}/details`
      )
      const response: Response<SectionForm[]> = data
      if (response?.succeeded) {
        commit(PatientMutations.SET_CURRENT_CONDITION, response.data)
        return true
      } else {
        return false
      }
    } catch (error) {
      commit(PatientMutations.SET_CURRENT_CONDITION, [])
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Set clinical history item
  async setCurrentConditionItem(
    { dispatch, commit },
    payload: CurrentConditionRequest
  ) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.patch(
        `${ApiVersion.V1}/Consultations/${payload.consultationId}/details`,
        payload.item
      )
      const response: Response<string> = data
      if (response.succeeded) {
        showToast(NotificationType.SUCCESS, response.message)
      } else {
        showToast(NotificationType.DANGER, response.message)
      }
      dispatch('getCurrentCondition', payload.consultationId)
      return response.succeeded
    } catch (error) {
      showToast(NotificationType.DANGER, 'Error al actualizar el campo')
      dispatch('getCurrentCondition', payload.consultationId)
      return false
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  },

  //Upload study file
  async uploadStudyFile({ commit, dispatch }, request: UploadStudyFileRequest) {
    commit('navigation/SET_LOADING_PAGE', true, { root: true })
    try {
      let InstFormData = new FormData()
      InstFormData.append('image', request.file)
      await axios.post(
        `${ApiVersion.V1}/Consultations/${request.consultationId}/${request.studyType}/lab-study`,
        InstFormData,
        {
          headers: { 'content-type': 'multipart/form-data' }
        }
      )
      dispatch('getCurrentCondition', request.consultationId)
    } catch (error) {
      console.error(error)
    } finally {
      commit('navigation/SET_LOADING_PAGE', false, { root: true })
    }
  },

  //Upload study file
  async uploadUltrasoundFile(
    { commit, dispatch },
    request: UploadUltrasoundFileRequest
  ) {
    commit(PatientMutations.SET_LOADER_PATIENT, true)
    try {
      console.log(request.fileType)
      let InstFormData = new FormData()
      InstFormData.append('file', request.file)
      await axios.post(
        `${ApiVersion.V1}/Consultations/${request.consultationId}/ultrasoundscan/${request.babyNumber}/upload-file/${request.fileType}`,
        InstFormData,
        {
          headers: { 'content-type': 'multipart/form-data' }
        }
      )
      dispatch('getCurrentCondition', request.consultationId)
    } catch (error) {
      console.error(error)
    } finally {
      commit(PatientMutations.SET_LOADER_PATIENT, false)
    }
  },

  //Delete ultrasound file
  async deleteUltrasoundFile(
    { commit, dispatch },
    request: DeleteUltrasoundFileRequest
  ) {
    commit(PatientMutations.SET_LOADER_PATIENT, true)
    try {
      await axios.delete(
        `${ApiVersion.V1}/Consultations/${request.consultationId}/ultrasoundscan/delete-file/${request.ultrasoundFileId}`
      )
      dispatch('getCurrentCondition', request.consultationId)
    } catch (error) {
      console.error(error)
    } finally {
      commit(PatientMutations.SET_LOADER_PATIENT, false)
    }
  },

  //Add transfusion
  async addTransfusion({ commit, dispatch }, request: TransfusionRequest) {
    commit(PatientMutations.SET_LOADER_PATIENT, true)
    try {
      await axios.post(
        `${ApiVersion.V1}/ClinicalHistories/${request.clinicalHistoryId}/blood-transfusions`,
        request
      )
      dispatch('getClinicalHistory', request.patientId)
    } catch (error) {
      console.error(error)
    } finally {
      commit(PatientMutations.SET_LOADER_PATIENT, false)
    }
  },

  //Set loader
  async setLoader({ commit }, value: Boolean) {
    commit(PatientMutations.SET_LOADER_PATIENT, value)
  },

  async setCurrentConditionLoader({ commit }, value: Boolean) {
    commit(PatientMutations.SET_LOADER_CURRENTCONDITION, value)
  },

  async getConsultoryDetails({ commit }, consultationId: Number) {
    try {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, true)
      const { data } = await axios.get(
        `${ApiVersion.V1}/Consultations/${consultationId}/consultory-details`
      )
      const response: Response<ConsultoryDetailsByConsultation> = data
      return response.data
    } catch (error) {
      commit(PatientMutations.SET_MORBIDITY_REGISTRY, [])
    } finally {
      commit(PatientMutations.SET_LOADER_CONSULTATIONS_LIST, false)
    }
  }
}
